import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ContentItem } from '../types/content';

interface FaqEntry {
  question: string;
  answer: string;
}

interface SupportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: ContentItem) => void;
  item: ContentItem | null;
  sectionId: string;
}

function parseContentValue(raw: any): any {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw.replace(/^"|"$/g, '');
  }
}

function isFaqArray(data: any): data is FaqEntry[] {
  return Array.isArray(data) && data.every(
    (i: any) => typeof i === 'object' && i !== null && 'question' in i && 'answer' in i
  );
}

export default function SupportContentModal({
  isOpen,
  onClose,
  onSave,
  item,
  sectionId,
}: SupportContentModalProps) {
  const [formData, setFormData] = useState<ContentItem>({
    id: crypto.randomUUID(),
    title: '',
    type: 'text',
    content: '',
    status: 'Draft',
  });
  const [faqs, setFaqs] = useState<FaqEntry[]>([]);
  const [useFaqEditor, setUseFaqEditor] = useState(false);

  useEffect(() => {
    if (item) {
      const content = parseContentValue(item.content);
      if (isFaqArray(content)) {
        setFaqs(content);
        setUseFaqEditor(true);
        setFormData({ ...item, content: '' });
      } else {
        setUseFaqEditor(false);
        setFaqs([]);
        setFormData({
          ...item,
          content: typeof content === 'object' ? JSON.stringify(content, null, 2) : (content ?? ''),
        });
      }
    } else {
      setUseFaqEditor(false);
      setFaqs([]);
      setFormData({ id: crypto.randomUUID(), title: '', type: 'text', content: '', status: 'Draft' });
    }
  }, [item, sectionId]);

  const handleTypeChange = (type: ContentItem['type']) => {
    setFormData({ ...formData, type });
    if (type === 'data') {
      setUseFaqEditor(true);
      if (faqs.length === 0) setFaqs([{ question: '', answer: '' }]);
    } else {
      setUseFaqEditor(false);
    }
  };

  const handleFaqChange = (index: number, field: keyof FaqEntry, value: string) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let content: string;
    if (useFaqEditor) {
      const valid = faqs.filter(f => f.question.trim() || f.answer.trim());
      content = JSON.stringify(valid);
    } else {
      const raw = formData.content;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try { JSON.parse(trimmed); content = trimmed; } catch { content = JSON.stringify(trimmed); }
        } else {
          content = JSON.stringify(trimmed);
        }
      } else {
        content = raw;
      }
    }
    onSave({ ...formData, content });
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium text-gray-900 mb-4">
                  {item ? 'Edit Content' : 'Add New Content'}
                </Dialog.Title>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Type</label>
                    <select
                      value={formData.type}
                      onChange={e => handleTypeChange(e.target.value as ContentItem['type'])}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                    >
                      <option value="text">Text</option>
                      <option value="media">Media</option>
                      <option value="data">FAQ List</option>
                    </select>
                  </div>

                  {useFaqEditor ? (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">FAQ Items</label>
                        <button
                          type="button"
                          onClick={() => setFaqs([...faqs, { question: '', answer: '' }])}
                          className="inline-flex items-center gap-1 text-sm text-golf-orange hover:text-golf-orange/80 transition-colors"
                        >
                          <PlusIcon className="w-4 h-4" />
                          Add Item
                        </button>
                      </div>
                      <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                        {faqs.map((faq, idx) => (
                          <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-400">#{idx + 1}</span>
                              <button
                                type="button"
                                onClick={() => setFaqs(faqs.filter((_, i) => i !== idx))}
                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                            <input
                              type="text"
                              placeholder="Question"
                              value={faq.question}
                              onChange={e => handleFaqChange(idx, 'question', e.target.value)}
                              className="mb-2 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                            />
                            <textarea
                              placeholder="Answer"
                              value={faq.answer}
                              onChange={e => handleFaqChange(idx, 'answer', e.target.value)}
                              rows={2}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                            />
                          </div>
                        ))}
                        {faqs.length === 0 && (
                          <p className="text-center py-4 text-sm text-gray-400">No items yet. Click "Add Item" above.</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Content</label>
                      <textarea
                        value={formData.content}
                        onChange={e => setFormData({ ...formData, content: e.target.value })}
                        rows={7}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange font-mono text-sm"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <select
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value as 'Published' | 'Draft' })}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                    >
                      <option value="Draft">Draft</option>
                      <option value="Published">Published</option>
                    </select>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
                    >
                      {item ? 'Save Changes' : 'Add Content'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
